/**
 * MobileNav Component
 * Full-screen navigation overlay for screens below @6xl breakpoint.
 * Contains navigation tabs, analytics tabs, and preferences (league + sportsbook).
 * Tools and account items live in the avatar dropdown instead.
 */

import { TierBadge } from '@components/ui/TierBadge';
import UpgradeModal from '@components/ui/UpgradeModal';
import { EU_SPORTSBOOKS, getSportsbookOptionsForLeague, SPORTSBOOKS } from '@config/constants';
import { useFeatureAccess } from '@hooks/useFeatureAccess';
import type { GatedFeature } from '@parlay-picker/shared/featureAccess';
import {
  getLeagueConfig,
  getLeagueLogoUrl,
  getOddsApiRegions,
  isInSeason,
  type LeagueKey,
  SUPPORTED_LEAGUE_KEYS,
} from '@parlay-picker/shared/league';
import {
  Activity,
  AlertCircle,
  BarChart2,
  BookOpen,
  Check,
  ChevronDown,
  FlaskConical,
  Globe,
  Layers,
  type LucideIcon,
  PieChart,
  Shield,
  Sparkles,
  Target,
  TrendingUp,
  Trophy,
  Users,
  X,
} from 'lucide-react';
import { type ReactElement, useState } from 'react';

type TabId =
  | 'picks'
  | 'parlays'
  | 'lab'
  | 'leaderboard'
  | 'search'
  | 'feed'
  | 'summary'
  | 'breakdown'
  | 'issues'
  | 'strategy'
  | 'market'
  | 'admin';

interface Tab {
  id: TabId;
  label: string;
}

/** Map each tab ID to a Lucide icon */
const TAB_ICONS: Record<TabId, LucideIcon> = {
  picks: Sparkles,
  parlays: Layers,
  leaderboard: Trophy,
  search: Users,
  feed: Activity,
  lab: FlaskConical,
  summary: BarChart2,
  breakdown: PieChart,
  issues: AlertCircle,
  strategy: Target,
  market: TrendingUp,
  admin: Shield,
};

interface MobileNavProps {
  isOpen: boolean;
  onClose: () => void;
  activeTab: TabId;
  setActiveTab: (tab: TabId) => void;
  primaryTabs: Tab[];
  analyticsTabs: Tab[];
  isAdmin: boolean;
  /** The book for `currentLeague`. */
  sportsbook: string;
  onSportsbookChange: (sportsbook: string) => void;
  shareUnviewedCount: number;
  currentLeague: LeagueKey;
  onLeagueChange: (key: LeagueKey) => void;
}

export function MobileNav({
  isOpen,
  onClose,
  activeTab,
  setActiveTab,
  primaryTabs,
  analyticsTabs,
  isAdmin,
  sportsbook,
  onSportsbookChange,
  shareUnviewedCount,
  currentLeague,
  onLeagueChange,
}: MobileNavProps): ReactElement | null {
  const { isLocked, isHidden: isFeatureHidden } = useFeatureAccess();
  const [upgradeFeature, setUpgradeFeature] = useState<GatedFeature | null>(null);
  const [leagueExpanded, setLeagueExpanded] = useState(false);
  const [sportsbookExpanded, setSportsbookExpanded] = useState(false);

  if (!isOpen) return null;

  const handleTabClick = (tabId: TabId): void => {
    const isAnalyticsTab = analyticsTabs.some((t) => t.id === tabId);
    if (isAnalyticsTab && tabId !== 'summary' && isLocked('analytics_advanced')) {
      setUpgradeFeature('analytics_advanced');
      return;
    }
    setActiveTab(tabId);
    onClose();
  };

  const sportsbooksList = getSportsbookOptionsForLeague(currentLeague);
  const selectedBook =
    sportsbooksList.find((book) => book.id === sportsbook) ??
    (getOddsApiRegions(currentLeague) === 'us' ? SPORTSBOOKS.fanduel : EU_SPORTSBOOKS.pinnacle);
  const currentLeagueConfig = getLeagueConfig(currentLeague);
  const currentLeagueLogoUrl = getLeagueLogoUrl(currentLeague);

  return (
    <div className="fixed inset-0 z-50 bg-theme-bg overflow-y-auto" data-testid="mobile-nav">
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-theme-border">
        <h2 className="text-lg font-bold text-theme-text">Menu</h2>
        <button
          type="button"
          data-testid="mobile-nav-close"
          onClick={onClose}
          className="p-2 rounded-lg hover:bg-theme-surface-hover transition-colors"
        >
          <X className="w-6 h-6 text-theme-text-secondary" />
        </button>
      </div>

      {/* Navigation */}
      <div className="p-4 space-y-6">
        {/* Primary tabs */}
        <div>
          <h3 className="px-4 text-xs font-semibold text-theme-text-tertiary uppercase tracking-wider mb-2">
            Navigation
          </h3>
          <div className="space-y-1">
            {primaryTabs.map((tab) => {
              const Icon = TAB_ICONS[tab.id];
              return (
                <button
                  type="button"
                  key={tab.id}
                  data-testid={`mobile-nav-${tab.id}`}
                  onClick={() => handleTabClick(tab.id)}
                  className={`w-full text-left px-4 py-3 rounded-lg text-base font-medium transition-colors flex items-center gap-3 ${
                    activeTab === tab.id
                      ? 'bg-theme-primary-fill text-theme-on-primary-fill'
                      : 'text-theme-text-secondary hover:bg-theme-surface-hover'
                  }`}
                >
                  <Icon className="w-5 h-5" />
                  <span className="flex-1">{tab.label}</span>
                  {tab.id === 'feed' && shareUnviewedCount > 0 && (
                    <span className="px-2 py-0.5 text-xs font-bold rounded-full bg-theme-primary-fill text-theme-on-primary-fill">
                      {shareUnviewedCount > 99 ? '99+' : shareUnviewedCount}
                    </span>
                  )}
                </button>
              );
            })}
            {!isFeatureHidden('lab_view') && (
              <button
                type="button"
                data-testid="mobile-nav-lab"
                onClick={() => handleTabClick('lab')}
                className={`w-full text-left px-4 py-3 rounded-lg text-base font-medium transition-colors flex items-center gap-3 ${
                  activeTab === 'lab'
                    ? 'bg-theme-primary-fill text-theme-on-primary-fill'
                    : 'text-theme-text-secondary hover:bg-theme-surface-hover'
                }`}
              >
                <FlaskConical className="w-5 h-5" />
                Lab
              </button>
            )}
            {isAdmin && (
              <button
                type="button"
                data-testid="mobile-nav-admin"
                onClick={() => handleTabClick('admin')}
                className={`w-full text-left px-4 py-3 rounded-lg text-base font-medium transition-colors flex items-center gap-3 ${
                  activeTab === 'admin'
                    ? 'bg-theme-primary-fill text-theme-on-primary-fill'
                    : 'text-theme-text-secondary hover:bg-theme-surface-hover'
                }`}
              >
                <Shield className="w-5 h-5" />
                Admin
              </button>
            )}
          </div>
        </div>

        {/* Analytics section */}
        <div>
          <h3 className="px-4 text-xs font-semibold text-theme-text-tertiary uppercase tracking-wider mb-2">
            Analytics
          </h3>
          <div className="space-y-1">
            {analyticsTabs.map((tab) => {
              const Icon = TAB_ICONS[tab.id];
              const locked = tab.id !== 'summary' && isLocked('analytics_advanced');
              return (
                <button
                  type="button"
                  key={tab.id}
                  data-testid={`mobile-nav-${tab.id}`}
                  onClick={() => handleTabClick(tab.id)}
                  className={`w-full text-left px-4 py-3 rounded-lg text-base font-medium transition-colors flex items-center gap-3 ${
                    activeTab === tab.id
                      ? 'bg-theme-primary-fill text-theme-on-primary-fill'
                      : locked
                        ? 'text-theme-text-tertiary hover:bg-theme-surface-hover'
                        : 'text-theme-text-secondary hover:bg-theme-surface-hover'
                  }`}
                >
                  <Icon className="w-5 h-5" />
                  <span className="flex-1">{tab.label}</span>
                  {locked && <TierBadge feature="analytics_advanced" size="md" />}
                </button>
              );
            })}
          </div>
        </div>

        {/* Preferences */}
        <div>
          <h3 className="px-4 text-xs font-semibold text-theme-text-tertiary uppercase tracking-wider mb-2">
            Preferences
          </h3>
          <div className="space-y-1">
            {/* League — inline expandable */}
            <div>
              <button
                type="button"
                data-testid="mobile-nav-league-expand"
                onClick={() => setLeagueExpanded((prev) => !prev)}
                className="w-full text-left px-4 py-3 rounded-lg text-base font-medium transition-colors text-theme-text-secondary hover:bg-theme-surface-hover flex items-center gap-3"
              >
                <Globe className="w-5 h-5" />
                <span className="flex-1">League</span>
                {currentLeagueLogoUrl && (
                  <img
                    src={currentLeagueLogoUrl}
                    alt=""
                    className="w-4 h-4 object-contain"
                    decoding="async"
                  />
                )}
                <span className="text-sm text-theme-text-tertiary">
                  {currentLeagueConfig.label}
                </span>
                <ChevronDown
                  className={`w-4 h-4 transition-transform ${leagueExpanded ? 'rotate-180' : ''}`}
                />
              </button>
              {leagueExpanded && (
                <div className="ml-8 mr-2 mt-1 mb-2 space-y-0.5">
                  {[...SUPPORTED_LEAGUE_KEYS]
                    .sort((a, b) => {
                      const aIn = isInSeason(a);
                      const bIn = isInSeason(b);
                      if (aIn === bIn) return 0;
                      return aIn ? -1 : 1;
                    })
                    .map((key) => {
                      const config = getLeagueConfig(key);
                      const logoUrl = getLeagueLogoUrl(key);
                      const inSeason = isInSeason(key);
                      const isSelected = currentLeague === key;
                      return (
                        <button
                          type="button"
                          key={key}
                          data-testid={`mobile-nav-league-${key}`}
                          onClick={() => {
                            onLeagueChange(key);
                            setLeagueExpanded(false);
                          }}
                          className={`w-full text-left px-3 py-2.5 rounded-lg text-sm transition-colors flex items-center gap-3 ${
                            isSelected
                              ? 'bg-theme-surface-active text-theme-text'
                              : !inSeason
                                ? 'text-theme-text-tertiary hover:bg-theme-surface-hover'
                                : 'text-theme-text-secondary hover:bg-theme-surface-hover'
                          }`}
                        >
                          {logoUrl && (
                            <img
                              src={logoUrl}
                              alt=""
                              className={`w-4 h-4 object-contain ${!inSeason ? 'opacity-40' : ''}`}
                              decoding="async"
                            />
                          )}
                          <span className="flex-1">
                            {config.label}
                            {!inSeason && (
                              <span className="ml-1.5 text-xs text-theme-text-tertiary">
                                Off-season
                              </span>
                            )}
                          </span>
                          {isSelected && <Check className="w-4 h-4 text-theme-success shrink-0" />}
                        </button>
                      );
                    })}
                </div>
              )}
            </div>

            {/* Sportsbook — inline expandable */}
            <div>
              <button
                type="button"
                onClick={() => setSportsbookExpanded((prev) => !prev)}
                className="w-full text-left px-4 py-3 rounded-lg text-base font-medium transition-colors text-theme-text-secondary hover:bg-theme-surface-hover flex items-center gap-3"
              >
                <BookOpen className="w-5 h-5" />
                <span className="flex-1">Sportsbook</span>
                <span className={`w-2 h-2 rounded-full shrink-0 ${selectedBook.dot}`} />
                <span className="text-sm text-theme-text-tertiary">{selectedBook.name}</span>
                <ChevronDown
                  className={`w-4 h-4 transition-transform ${sportsbookExpanded ? 'rotate-180' : ''}`}
                />
              </button>
              {sportsbookExpanded && (
                <div className="ml-8 mr-2 mt-1 mb-2 space-y-0.5">
                  {sportsbooksList.map((book) => {
                    const isDisabled = !book.oddsApiSupported;
                    const isSelected = sportsbook === book.id;
                    return (
                      <button
                        type="button"
                        key={book.id}
                        onClick={() => {
                          if (!isDisabled) {
                            onSportsbookChange(book.id);
                            setSportsbookExpanded(false);
                          }
                        }}
                        disabled={isDisabled}
                        title={
                          isDisabled
                            ? 'Account linking only - not available for pick generation'
                            : undefined
                        }
                        className={`w-full text-left px-3 py-2.5 rounded-lg text-sm transition-colors flex items-center gap-3 ${
                          isDisabled
                            ? 'text-theme-text-tertiary opacity-50 cursor-not-allowed'
                            : isSelected
                              ? 'bg-theme-surface-active text-theme-text'
                              : 'text-theme-text-secondary hover:bg-theme-surface-hover'
                        }`}
                      >
                        <span
                          className={`w-2.5 h-2.5 rounded-full shrink-0 ${isDisabled ? 'bg-theme-border' : book.dot}`}
                        />
                        <span className="flex-1">{book.name}</span>
                        {isSelected && !isDisabled && (
                          <Check className="w-4 h-4 text-theme-success shrink-0" />
                        )}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {upgradeFeature && (
        <UpgradeModal
          mode="feature"
          feature={upgradeFeature}
          onDismiss={() => setUpgradeFeature(null)}
        />
      )}
    </div>
  );
}
